import { Button, Devices, Facility } from "@app/shared";
import { useCallback, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { RequiresGoogleMaps } from "../../shared/maps/RequiresGoogleMaps";
import { LayoutDesigner, LayoutDesignerMethods } from "../../shared/LayoutDesigner/LayoutDesigner";
import { AttenuatorLayoutEditor } from "../../shared/LayoutDesigner/AttenuatorLayoutEditor";
import { isAttenuatorFacility, hasAttenuatorDevice } from "../../shared/LayoutDesigner/attenuatorLayoutHelpers";

type FacilityLayoutInfoProps = {
    facility: Facility;
    onChange: () => void;
};
export const FacilityLayoutInfo: React.FC<FacilityLayoutInfoProps> = ({ facility, onChange }) => {
    const designerRef = useRef<LayoutDesignerMethods>(null);
    const [dismissed, setDismissed] = useState<Set<string>>(new Set());
    // Set by device provisioning, which sends the user straight here to calibrate the new device.
    // Clear it only once the designer has actually opened alignment, not on mount: provisioning
    // refreshes the facility as it hands off, and FacilityDetails renders a loading screen while
    // that is in flight, so this component mounts, unmounts and mounts again. Stripping on the
    // first mount would leave the second — the one that can see the new device — with nothing to
    // act on. TabbedDashLayout carries params across tab switches, so it does have to go.
    const [searchParams, setSearchParams] = useSearchParams();
    const alignDeviceId = searchParams.get("alignDevice") ?? undefined;
    const clearAlignParam = useCallback(() => {
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete("alignDevice");
                return next;
            },
            { replace: true },
        );
    }, [setSearchParams]);

    if (isAttenuatorFacility(facility)) {
        return <AttenuatorLayoutEditor facility={facility} onChange={onChange} />;
    }

    const now = Date.now();
    const collectionsDone = (facility.devices ?? []).filter(
        (d) =>
            !!d.id &&
            !dismissed.has(d.id) &&
            !!d.laneCollectionUntil &&
            d.laneCollectionUntil.getTime() <= now &&
            (!d.laneCollectionNotifiedUntil || d.laneCollectionNotifiedUntil.getTime() !== d.laneCollectionUntil.getTime())
    );

    const acknowledge = (deviceId: string) => {
        setDismissed((prev) => new Set(prev).add(deviceId));
        void Devices.acknowledgeLaneCollection(deviceId);
    };

    return (
        <>
            {hasAttenuatorDevice(facility) && (
                <div className="bg-amber-50 border border-amber-300 text-amber-800 rounded-md px-4 py-2 mb-4 text-sm">
                    This location has a mix of attenuator truck and other device modes. The map-based layout editor is shown, but attenuator devices may not benefit from it.
                </div>
            )}

            {collectionsDone.map((d) => (
                <div key={d.id} className="bg-blue-50 border border-blue-300 text-blue-900 rounded-md px-4 py-3 mb-4 text-sm flex flex-wrap items-center justify-between gap-3">
                    <span>
                        Lane collection finished for <strong>{d.name ?? "this device"}</strong>. The collected traffic is ready — review the suggested lane lines.
                    </span>
                    <span className="flex gap-2 shrink-0">
                        <Button onClick={() => acknowledge(d.id!)}>Dismiss</Button>
                        <Button
                            className="border-brand-secondary border-2"
                            onClick={() => {
                                acknowledge(d.id!);
                                designerRef.current?.openCollectAndGenerate();
                            }}
                        >
                            Review
                        </Button>
                    </span>
                </div>
            ))}

            <RequiresGoogleMaps>
                <LayoutDesigner ref={designerRef} allowEdit={true} mapMargins={{ bottom: 200, left: 0, right: 0, top: 0 }} facility={facility} onChange={onChange} hideWarning={facility.originReferencePointLatLng?.origin?.x !== 0 || !facility.backgroundImageSource?.length} defaultDeviceId={alignDeviceId} autoAlignDeviceId={alignDeviceId} onAutoAlignOpened={clearAlignParam} />
            </RequiresGoogleMaps>
        </>
    );
};
